import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';

const parseOptions = (children) => {
  const options = [];
  let placeholder = null;

  React.Children.forEach(children, (child) => {
    if (!child || !child.props) {
      return;
    }
    if (child.props.disabled) {
      placeholder = child.props.children;
      return;
    }
    options.push({
      value: child.props.value !== undefined ? child.props.value : child.props.children,
      label: child.props.children,
      symbol: child.props.symbol,
      unit: child.props.unit,
    });
  });

  return { options, placeholder };
};

const resolveSelection = (value, options, placeholder) => {
  if (value === '' || value === null || value === undefined) {
    return { display: placeholder || '', resolvedValue: '' };
  }

  const byValue = options.find((o) => String(o.value) === String(value));
  if (byValue) {
    return { display: byValue.label, resolvedValue: byValue.value };
  }

  const byLabel = options.find((o) => String(o.label) === String(value));
  if (byLabel) {
    return { display: byLabel.label, resolvedValue: byLabel.value };
  }

  return { display: placeholder || String(value), resolvedValue: value };
};

const BrandSelect = ({ children, name, value, onChange, disabled, className, toggleClassName, id }) => {
  const [open, setOpen] = useState(false);
  const { options, placeholder } = useMemo(() => parseOptions(children), [children]);
  const { display, resolvedValue } = useMemo(
    () => resolveSelection(value, options, placeholder),
    [value, options, placeholder]
  );

  const handleSelect = (optionValue) => {
    if (disabled) {
      return;
    }
    const selected = options.find((o) => String(o.value) === String(optionValue));
    onChange({
      target: {
        name,
        value: optionValue,
        id,
        type: 'select',
        symbol: selected?.symbol,
        unit: selected?.unit,
      },
    });
    setOpen(false);
  };

  return (
    <Dropdown
      isOpen={open}
      toggle={() => !disabled && setOpen((prev) => !prev)}
      className={`brand-select w-100 ${className || ''}`}
    >
      <DropdownToggle
        tag="div"
        caret
        disabled={disabled}
        className={`brand-select__toggle form-control dropdown-toggle ${toggleClassName || ''} ${disabled ? 'disabled' : ''}`}
      >
        <span className="brand-select__label">{display}</span>
      </DropdownToggle>
      <DropdownMenu className="brand-select__menu">
        {options.map((opt) => (
          <DropdownItem
            key={`${name}-${String(opt.value)}`}
            toggle={false}
            active={String(resolvedValue) === String(opt.value)}
            onClick={() => handleSelect(opt.value)}
          >
            {opt.label}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
};

BrandSelect.propTypes = {
  children: PropTypes.node,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  toggleClassName: PropTypes.string,
  id: PropTypes.string,
};

BrandSelect.defaultProps = {
  disabled: false,
};

export default BrandSelect;
